import { Fragment, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Button, useBreakpointValue } from '@skin-seoul/design-system';
import type { Review, ReviewImage } from '..';
import { StarIcon } from '../assets/icons/StarIcon';
import { ReviewCardImageCarousel } from './ReviewCardImageCarousel';
import { formatRelativeTime } from '@skin-seoul/service/src/utils/review/dateUtils';
import avatar from '../assets/images/avatar.png';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface ReviewCardProps {
  review: Review;
  onImageClick: (image: ReviewImage, index: number) => void;
  onViewMore?: (review: Review) => void;
  /** The card's image carousel settled on a different image (SSD-5573). */
  onImageIndexChange?: (review: Review, toIndex: number) => void;
  isLastInColumn?: boolean;
}

export const ReviewCard = ({
  review,
  onImageClick,
  onViewMore,
  onImageIndexChange,
  isLastInColumn: _isLastInColumn = false,
}: ReviewCardProps) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const hasReviewImages = Boolean(review.images && review.images.length > 0);

  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTextClamped, setIsTextClamped] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const checkTextClamp = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTextClamped(element.scrollHeight > element.clientHeight);
      }
    };
    const timeoutId = setTimeout(() => {
      checkTextClamp();
    });
    window.addEventListener('resize', checkTextClamp);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTextClamp);
    };
  }, [review.content]);

  return (
    <div className="flex gap-[13px] w-full">
      <div className="p-4 rounded-[8px] border border-[#E9EAEB] w-full grow h-fit tablet:h-[336px]">
        <div className="flex flex-col gap-4 items-start h-full">
          <div className="flex flex-row justify-between items-center w-full">
            <div className="flex flex-row gap-1 items-center">
              <p className="text-heading-h5 font-semibold text-primary-400">{review.rating}</p>
              <StarIcon width={20} height={20} />
            </div>

            <p className="text-neutral-400 text-label-small font-normal">{formatRelativeTime(review.createdAt)}</p>
          </div>
          {isMobile && hasReviewImages && (
            <ReviewCardImageCarousel
              images={review.images || []}
              width="full"
              height="358px"
              onImageClick={onImageClick}
              onIndexChange={(toIndex) => onImageIndexChange?.(review, toIndex)}
            />
          )}
          <div className="flex flex-col gap-4 tablet:gap-3 items-start w-full h-full justify-between">
            <div className="flex flex-col items-start gap-4 tablet:gap-2">
              <p className="text-paragraph-large font-semibold text-neutral-800">{review.title}</p>
              <p ref={textRef} className="text-paragraph-small font-normal text-neutral-500 line-clamp-3">
                {review.content}
              </p>
              {isTextClamped && (
                <div>
                  <Button
                    className="max-w-[fit-content] max-h-[fit-content] text-primary-400 text-label-small px-0"
                    variant="plain"
                    onClick={() => onViewMore?.(review)}
                  >
                    View more
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-row rounded-[8px] p-2 bg-neutral-50 w-full gap-2">
              <img src={avatar.src} alt="avatar" className="w-8 h-8 rounded-full shrink-0" />
              <div className="flex flex-col items-start gap-0.5 w-full">
                <p className="text-label-small font-medium text-neutral-800">{review.author.name}</p>
                <div className="flex flex-row gap-1 flex-wrap">
                  {[...review.skinType, ...review.skinConcern].map((type, index, array) => (
                    <Fragment key={index}>
                      <p className="text-label-xsmall font-normal text-neutral-700">{type}</p>
                      {index < array.length - 1 && <div className="w-0.5 h-0.5 bg-neutral-100 rounded-full" />}
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isMobile ? (
        hasReviewImages ? (
          <ReviewCardImageCarousel
            images={review.images || []}
            width="279px"
            height="336px"
            onImageClick={onImageClick}
            onIndexChange={(toIndex) => onImageIndexChange?.(review, toIndex)}
          />
        ) : (
          <div className="w-[279px] shrink-0" />
        )
      ) : null}
    </div>
  );
};

export default ReviewCard;
